import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, ArrowLeft, User, Phone, Mail, MapPin, FileText, Calendar, DollarSign, Droplet, Zap, Thermometer, Wrench, Sparkles, Car, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceIcons = {
  plumbing: Droplet,
  electrical: Zap,
  hvac: Thermometer,
  handyman: Wrench,
  cleaning: Sparkles,
  mechanics: Car,
};

const RequestServicePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [matchedWorker, setMatchedWorker] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    service_category: searchParams.get("service") || "",
    description: "",
    location: "",
    preferred_date: "",
    budget: ""
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    return formData.service_category && formData.description && formData.location;
  };

  const validateStep2 = () => {
    return formData.customer_name && formData.customer_email && formData.customer_phone;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/service-requests`, formData);
      setSubmitted(true);
      
      // Check if worker was matched
      if (res.data.matched_worker_id) {
        try {
          const workerRes = await axios.get(`${API}/workers/${res.data.matched_worker_id}`);
          setMatchedWorker(workerRes.data);
        } catch (e) {
          console.log("Could not fetch matched worker");
        }
      }
      
      toast.success("Service request submitted successfully!");
    } catch (error) {
      console.error("Error submitting request:", error);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white" data-testid="request-success-page">
        <Navbar />
        <div className="container-custom py-20">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-[#e6f4ea] flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#1e8e3e]" />
            </div>
            <h1 className="text-3xl font-medium text-[#202124] mb-4">
              {t("requestService.successTitle")}
            </h1>
            <p className="text-[#5f6368] mb-8">
              {t("requestService.successMessage")}
            </p>

            {matchedWorker && (
              <div className="bg-[#f8f9fa] rounded-2xl p-6 mb-8 text-left" data-testid="matched-worker-card">
                <div className="text-sm font-medium text-[#1a73e8] mb-3">{t("requestService.matchedPro")}</div>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-[#e8f0fe] flex items-center justify-center">
                    <span className="text-xl font-medium text-[#1a73e8]">
                      {matchedWorker.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium text-[#202124]">{matchedWorker.name}</h3>
                    <p className="text-sm text-[#5f6368]">{matchedWorker.location}</p>
                  </div>
                </div>
                <p className="text-sm text-[#5f6368] mb-4">
                  {matchedWorker.name} {t("requestService.willContact")}
                </p>
                <div className="flex gap-2">
                  <a
                    href={`tel:${matchedWorker.phone}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#1e8e3e] text-white rounded-full text-sm font-medium hover:bg-[#1a7c37]"
                  >
                    <Phone className="w-4 h-4" />
                    {t("common.callNow")}
                  </a>
                  <a
                    href={`https://wa.me/${matchedWorker.phone?.replace(/\D/g, '')}?text=Hi ${matchedWorker.name}, I just submitted a ${formData.service_category} service request on LocalPros. Can you help me?`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#25D366] text-white rounded-full text-sm font-medium hover:bg-[#20bd5a]"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </a>
                </div>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate("/browse")}
                className="rounded-full px-6 border-[#dadce0]"
                data-testid="browse-more-btn"
              >
                {t("nav.browse")}
              </Button>
              <Button
                onClick={() => navigate("/")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-6"
                data-testid="back-home-btn"
              >
                {t("common.backToHome")}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="request-service-page">
      <Navbar />

      <div className="container-custom py-12">
        <div className="max-w-2xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-12">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm transition-colors ${
                    step >= s
                      ? "bg-[#1a73e8] text-white"
                      : "bg-[#f1f3f4] text-[#5f6368]"
                  }`}
                >
                  {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                </div>
                {s < 2 && (
                  <div
                    className={`w-20 h-1 mx-2 rounded ${
                      step > s ? "bg-[#1a73e8]" : "bg-[#dadce0]"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Service Details */}
          {step === 1 && (
            <div className="form-container" data-testid="step-1-form">
              <h2 className="text-2xl font-medium text-[#202124] mb-2">
                {t("requestService.step1Title")}
              </h2>
              <p className="text-[#5f6368] mb-8">
                {t("requestService.step1Subtitle")}
              </p>

              {/* Service Category Selection */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#202124] mb-3 block">
                  {t("forms.selectService")} *
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {categories.map((cat) => {
                    const Icon = serviceIcons[cat.slug];
                    const isSelected = formData.service_category === cat.slug;
                    return (
                      <button
                        key={cat.slug}
                        type="button"
                        onClick={() => handleInputChange("service_category", cat.slug)}
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          isSelected
                            ? "border-[#1a73e8] bg-[#e8f0fe]"
                            : "border-[#dadce0] hover:border-[#1a73e8]/50"
                        }`}
                        data-testid={`service-option-${cat.slug}`}
                      >
                        {Icon && <Icon className={`w-6 h-6 ${isSelected ? "text-[#1a73e8]" : "text-[#5f6368]"}`} />}
                        <span className={`text-sm font-medium ${isSelected ? "text-[#1a73e8]" : "text-[#202124]"}`}>
                          {cat.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#202124] mb-2 block">
                  <FileText className="w-4 h-4 inline mr-2" />
                  {t("forms.description")} *
                </Label>
                <Textarea
                  placeholder={t("forms.descriptionPlaceholder")}
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className="min-h-[120px] rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                  data-testid="description-input"
                />
              </div>

              {/* Location */}
              <div className="mb-6">
                <Label className="text-sm font-medium text-[#202124] mb-2 block">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  {t("forms.location")} *
                </Label>
                <Input
                  placeholder={t("forms.locationPlaceholder")}
                  value={formData.location}
                  onChange={(e) => handleInputChange("location", e.target.value)}
                  className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                  data-testid="location-input"
                />
              </div>

              {/* Optional Fields */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div>
                  <Label className="text-sm font-medium text-[#202124] mb-2 block">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    {t("forms.preferredDate")}
                  </Label>
                  <Input
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => handleInputChange("preferred_date", e.target.value)}
                    className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                    data-testid="date-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#202124] mb-2 block">
                    <DollarSign className="w-4 h-4 inline mr-2" />
                    {t("forms.budget")}
                  </Label>
                  <Input
                    placeholder={t("forms.budgetPlaceholder")}
                    value={formData.budget}
                    onChange={(e) => handleInputChange("budget", e.target.value)}
                    className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                    data-testid="budget-input"
                  />
                </div>
              </div>

              <Button
                onClick={() => setStep(2)}
                disabled={!validateStep1()}
                className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full py-3 h-auto text-base font-medium"
                data-testid="next-step-btn"
              >
                {t("common.continue")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Contact Info */}
          {step === 2 && (
            <div className="form-container" data-testid="step-2-form">
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 text-[#5f6368] hover:text-[#202124] mb-6 text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("common.back")}
              </button>

              <h2 className="text-2xl font-medium text-[#202124] mb-2">
                {t("requestService.step2Title")}
              </h2>
              <p className="text-[#5f6368] mb-8">
                {t("requestService.step2Subtitle")}
              </p>

              <div className="space-y-6">
                <div>
                  <Label className="text-sm font-medium text-[#202124] mb-2 block">
                    <User className="w-4 h-4 inline mr-2" />
                    {t("forms.name")} *
                  </Label>
                  <Input
                    placeholder="John Doe"
                    value={formData.customer_name}
                    onChange={(e) => handleInputChange("customer_name", e.target.value)}
                    className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#202124] mb-2 block">
                    <Mail className="w-4 h-4 inline mr-2" />
                    {t("forms.email")} *
                  </Label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.customer_email}
                    onChange={(e) => handleInputChange("customer_email", e.target.value)}
                    className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#202124] mb-2 block">
                    <Phone className="w-4 h-4 inline mr-2" />
                    {t("forms.phone")} *
                  </Label>
                  <Input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={formData.customer_phone}
                    onChange={(e) => handleInputChange("customer_phone", e.target.value)}
                    className="h-12 rounded-xl border-[#dadce0] focus:ring-[#1a73e8]"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div className="mt-8">
                <Button
                  onClick={handleSubmit}
                  disabled={!validateStep2() || loading}
                  className="w-full bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full py-3 h-auto text-base font-medium"
                  data-testid="submit-request-btn"
                >
                  {loading ? t("common.submitting") : t("common.submitRequest")}
                </Button>
                <p className="text-xs text-[#5f6368] text-center mt-4">
                  {t("forms.termsAgreement")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RequestServicePage;
